export type NodeKind = 'folder' | 'bookmark' | 'separator';

/** Canonical top-level containers. Every browser root maps onto one of these. */
export type RootKey = 'toolbar' | 'menu' | 'other' | 'mobile';
export const ROOT_KEYS: readonly RootKey[] = ['toolbar', 'menu', 'other', 'mobile'];

export const ROOT_TITLES: Record<RootKey, string> = {
  toolbar: 'Bookmarks bar',
  menu: 'Bookmarks menu',
  other: 'Other bookmarks',
  mobile: 'Mobile bookmarks',
};

export const rootId = (key: RootKey): string => `root:${key}`;
export const isRootId = (id: string): boolean => id.startsWith('root:');
export const rootKeyOf = (id: string): RootKey | null => {
  const key = id.slice(5) as RootKey;
  return isRootId(id) && ROOT_KEYS.includes(key) ? key : null;
};
/** Where nodes end up when their parent chain is broken. */
export const FALLBACK_PARENT = 'root:other';

export type BrowserKind = 'chrome' | 'firefox' | 'web';

/**
 * Server-side bookmark record. Each field group (content, location, deletion)
 * carries its own hybrid logical clock so concurrent edits of different
 * aspects of the same node merge instead of overwriting each other.
 */
export interface BookmarkRow {
  id: string;
  kind: NodeKind;
  parentId: string;
  position: string;
  title: string;
  url: string | null;
  contentHlc: string;
  locationHlc: string;
  deleteFlag: boolean;
  deletedHlc: string;
  /** Derived: deleteFlag && deletedHlc > max(contentHlc, locationHlc). */
  deleted: boolean;
  rev: number;
  updatedAt: string;
  updatedBy: string | null;
}

/** A client-authored mutation of one node. Absent groups are left untouched. */
export interface BookmarkChange {
  id: string;
  /** Required when the node may not exist on the server yet. */
  kind?: NodeKind;
  content?: { title: string; url: string | null; hlc: string };
  location?: { parentId: string; position: string; hlc: string };
  deletion?: { deleted: boolean; hlc: string };
}

export type RejectReason = 'cycle' | 'invalid_parent' | 'missing_kind' | 'kind_mismatch' | 'invalid_value';

export interface PushResult {
  rows: BookmarkRow[];
  rejected: { id: string; reason: RejectReason }[];
  rev: number;
}

export interface SyncStateRow {
  rev: number;
  gcRev: number;
}

export interface DeviceRecord {
  id: string;
  name: string;
  browser: BrowserKind;
  platform: string | null;
  userAgent: string | null;
  appVersion: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  lastSyncAt: string | null;
}

export type ExtensionType = 'extension' | 'theme' | 'hosted_app' | 'packaged_app' | 'legacy_packaged_app' | 'login_screen_extension' | 'other';

export interface InstalledExtension {
  extId: string;
  name: string;
  version: string;
  enabled: boolean;
  type: ExtensionType;
  installType: string;
  homepageUrl: string | null;
  description: string | null;
}

export interface DeviceExtension extends InstalledExtension {
  deviceId: string;
  updatedAt: string;
}

export function isDeleted(r: Pick<BookmarkRow, 'deleteFlag' | 'deletedHlc' | 'contentHlc' | 'locationHlc'>): boolean {
  if (!r.deleteFlag) return false;
  const newestEdit = r.contentHlc > r.locationHlc ? r.contentHlc : r.locationHlc;
  return r.deletedHlc > newestEdit;
}
